import React from "react";
import clsx from "clsx";
import { isNil, isObject, has } from "lodash";
import InputBase from "@material-ui/core/InputBase";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTheme } from "@material-ui/core/styles";
import useMessageInputStyles from "../messageInputStyles";
import { i18n } from "../../../translate/i18n";

const CustomInput = (props) => {
  const {
    loading,
    inputRef,
    ticketStatus,
    inputMessage,
    setInputMessage,
    handleSendMessage,
    handleInputPaste,
    disableOption,
    handleQuickAnswersClick,
    options,
    popupOpen,
    inputWrapperClassName
  } = props;
  const classes = useMessageInputStyles();
  const theme = useTheme();

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.nativeEvent?.isComposing) return;
      if (loading) return;
      if (e.shiftKey) return;
      if (popupOpen) return;
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
      return;
    }

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      if (popupOpen) {
        return;
      }
      e.stopPropagation();
      return;
    }

    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && e.ctrlKey) {
      e.stopPropagation();
      return;
    }

    if (e.key === "Home" || e.key === "End") {
      e.stopPropagation();
      return;
    }

    if ((e.key === "Home" || e.key === "End") && e.ctrlKey) {
      e.stopPropagation();
      return;
    }

    if (e.key === "PageUp" || e.key === "PageDown") {
      e.stopPropagation();
      return;
    }

    if (e.key === "a" && e.ctrlKey) {
      e.stopPropagation();
      return;
    }
  };

  const onPaste = (e) => {
    if (ticketStatus === "open") {
      handleInputPaste(e);
    }
  };

  const renderPlaceholder = () => {
    if (ticketStatus === "open") {
      return i18n.t("messagesInput.placeholderOpen") + " (usa / para respuestas rápidas)";
    }
    return i18n.t("messagesInput.placeholderClosed");
  };

  const setInputRef = (input) => {
    if (input) {
      input.focus();
      inputRef.current = input;

      const handleDirectKeyDown = (e) => {
        if (!popupOpen) {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            return;
          }

          if (e.key === "Home" || e.key === "End" ||
              e.key === "PageUp" || e.key === "PageDown" ||
              ((e.key === "ArrowUp" || e.key === "ArrowDown") && e.ctrlKey) ||
              ((e.key === "Home" || e.key === "End") && e.ctrlKey)) {
            return;
          }
        }
      };

      input.addEventListener('keydown', handleDirectKeyDown);

      return () => {
        input.removeEventListener('keydown', handleDirectKeyDown);
      };
    }
  };

  return (
    <div className={clsx(classes.messageInputWrapper, inputWrapperClassName)}>
      <Autocomplete
        freeSolo
        open={popupOpen}
        id="quick-messages-autocomplete"
        value={inputMessage}
        options={options}
        closeIcon={null}
        disablePortal={false}
        disableClearable={true}
        disableCloseOnSelect={true}
        PopperComponent={(props) => {
          const { anchorEl, open, ...domProps } = props;

          const inputElement = document.querySelector('.messageInputWrapper input') ||
                              document.querySelector('[placeholder*="respuestas rápidas"]') ||
                              document.querySelector('input[multiline]');
          const inputRect = inputElement ? inputElement.getBoundingClientRect() : null;

          const currentTheme = window.document.documentElement.classList.contains('dark-mode') ? 'dark' : 'light';

          let dropdownStyle = {
            ...domProps.style,
            zIndex: 9999,
            position: 'fixed',
            maxHeight: '250px',
            overflow: 'auto',
            backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#ffffff',
            border: `1px solid ${currentTheme === 'dark' ? '#4B5563' : '#e0e0e0'}`,
            borderRadius: '8px',
            boxShadow: currentTheme === 'dark' ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.15)',
            minHeight: '40px',
            padding: '8px 0'
          };

          if (inputRect) {
            const optionsCount = options ? options.length : 0;
            const dropdownHeight = Math.min(250, Math.max(40, (optionsCount * 44) + 16));

            dropdownStyle.top = `${inputRect.top - dropdownHeight - 8}px`;
            dropdownStyle.left = `${inputRect.left}px`;
            dropdownStyle.width = `${Math.max(inputRect.width, 350)}px`;
            dropdownStyle.bottom = 'auto';
            dropdownStyle.right = 'auto';
            dropdownStyle.height = `${dropdownHeight}px`;

            if (inputRect.top - dropdownHeight - 8 < 0) {
              dropdownStyle.top = `${inputRect.bottom + 8}px`;
              dropdownStyle.height = `${Math.min(200, window.innerHeight - inputRect.bottom - 16)}px`;
              dropdownStyle.maxHeight = `${Math.min(200, window.innerHeight - inputRect.bottom - 16)}px`;
            }

            if (inputRect.left + inputRect.width > window.innerWidth) {
              dropdownStyle.left = `${window.innerWidth - Math.max(inputRect.width, 350) - 8}px`;
            }

            if (inputRect.left < 0) {
              dropdownStyle.left = '8px';
            }
          } else {
            dropdownStyle.bottom = '80px';
            dropdownStyle.left = '50%';
            dropdownStyle.transform = 'translateX(-50%)';
            dropdownStyle.width = '350px';
            dropdownStyle.top = 'auto';
            dropdownStyle.right = 'auto';
          }

          return (
            <div
              {...domProps}
              className={classes.quickMessagesDropdown}
              style={dropdownStyle}
            />
          );
        }}
        getOptionLabel={(option) => {
          if (isObject(option)) {
            return option.label;
          } else {
            return option;
          }
        }}
        renderOption={(option) => {
          if (isObject(option)) {
            return (
              <div style={{
                padding: "12px 16px",
                fontSize: "14px",
                minHeight: "44px",
                lineHeight: "1.4",
                borderBottom: `1px solid ${theme.mode === 'dark' ? '#374151' : '#f0f0f0'}`,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "transparent",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: "8px"
                }}>
                  <span style={{
                    color: theme.mode === 'dark' ? '#60a5fa' : "#2196f3",
                    fontWeight: "500",
                    fontSize: "12px",
                    backgroundColor: theme.mode === 'dark' ? '#1e3a8a' : "#e3f2fd",
                    padding: "2px 6px",
                    borderRadius: "4px"
                  }}>
                    {option.shortcode}
                  </span>
                  <span style={{
                    color: theme.mode === 'dark' ? '#f9fafb' : "#333",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}>
                    {option.message}
                  </span>
                </div>
              </div>
            );
          } else {
            return (
              <div style={{
                padding: "12px 16px",
                fontSize: "14px",
                minHeight: "44px",
                lineHeight: "1.4",
                borderBottom: `1px solid ${theme.mode === 'dark' ? '#374151' : '#f0f0f0'}`,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                color: theme.mode === 'dark' ? '#f9fafb' : '#1f2937',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              >
                {option}
              </div>
            );
          }
        }}
        noOptionsText={
          <div style={{
            padding: "16px",
            textAlign: "center",
            color: theme.mode === 'dark' ? '#9ca3af' : "#666",
            fontSize: "14px"
          }}>
            <span role="img" aria-label="búsqueda">🔍</span> No se encontraron respuestas rápidas
          </div>
        }
        onChange={(event, opt) => {
          if (isObject(opt) && has(opt, "value") && isNil(opt.mediaPath)) {
            setInputMessage(opt.value);
            setTimeout(() => {
              inputRef.current.scrollTop = inputRef.current.scrollHeight;
            }, 200);
          } else if (isObject(opt) && has(opt, "value") && !isNil(opt.mediaPath)) {
            handleQuickAnswersClick(opt);
            setTimeout(() => {
              inputRef.current.scrollTop = inputRef.current.scrollHeight;
            }, 200);
          }
        }}
        onInputChange={(event, opt, reason) => {
          if (reason === "input") {
            setInputMessage(event.target.value);
          }
        }}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        style={{ width: "100%" }}
        ListboxProps={{
          onKeyDown: (e) => {
            if (popupOpen) {
              return;
            }
            e.stopPropagation();
          }
        }}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params;
          return (
            <InputBase
              {...params.InputProps}
              {...rest}
              disabled={disableOption()}
              inputRef={setInputRef}
              placeholder={renderPlaceholder()}
              multiline
              className={classes.messageInput}
              maxRows={5}
              onKeyDown={onKeyDown}
            />
          );
        }}
      />
    </div>
  );
};

export default CustomInput;
